/**
 * Utilities for handling message import/export operations
 */

import { Message, Project } from '../types';

/**
 * Escapes special characters in CSV fields
 */
export const escapeCSVField = (content: string | number): string => {
  if (typeof content !== 'string') {
    content = String(content);
  }
  
  if (/[,"\n\r]/.test(content)) {
    return `"${content.replace(/"/g, '""')}"`; // Double quotes are escaped as double-double quotes
  }
  return content;
};

/**
 * Formats timestamp in YYYY-MM-DD HH:mm:ss format
 */
export const formatTimestamp = (timestamp: string | undefined): string => {
  if (!timestamp) return '';
  
  try {
    const date = new Date(timestamp);
    
    // Format to YYYY-MM-DD HH:mm:ss
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  } catch (e) {
    console.error('Error formatting timestamp:', e);
    return timestamp; // Return original if parsing fails
  }
};

/**
 * Generates CSV content from project messages
 */
export const generateMessagesCSV = (project: Project | null): string | null => {
  if (!project || !project.messages || project.messages.length === 0) {
    return null;
  }

  // Create header row
  const headerRow = ['projectId', 'projectName', 'timestamp', 'createdAt', 'sender', 'text', 'comment', 'rating'].join(',');
  
  // Sort by effective timestamp (timestamp > createdAt)
  const sortedMessages = [...project.messages].sort((a, b) => {
    const at = new Date(a.timestamp || a.createdAt || '').getTime() || Number.POSITIVE_INFINITY;
    const bt = new Date(b.timestamp || b.createdAt || '').getTime() || Number.POSITIVE_INFINITY;
    return at - bt;
  });

  // Create data rows
  const dataRows = sortedMessages.map(message => {
    const projectId = String(project.id || '');
    const projectName = escapeCSVField(project.name || '');
    // Use the stored timestamp from the message, with fallbacks
    const timestamp = message.timestamp || message.createdAt || new Date().toISOString();
    // Include the createdAt field separately, formatted as YYYY-MM-DD HH:mm:ss
    const createdAt = formatTimestamp(message.createdAt);
    const sender = message.isBot ? (message.sender || 'Assistant') : 'You';
    const text = escapeCSVField(message.text);
    const comment = message.comment ? escapeCSVField(message.comment) : '';
    const rating = message.rating || 'none';
    
    return [projectId, projectName, timestamp, createdAt, sender, text, comment, rating].join(',');
  });
  
  // Combine header and data rows
  return [headerRow, ...dataRows].join('\n');
};

/**
 * Generate CSV content for project settings
 */
export const generateProjectSettingsCSV = (project: any): string | null => {
  if (!project) {
    return null;
  }

  // Create header row
  const headerRow = ['name', 'base_prompt', 'model_name', 'chat_type', 'rag_api_url', 'rag_confidence_threshold', 'rag_token_limit', 'rag_top_k', 'rag_vector_db', 'intro_message'].join(',');
  
  // Create content row - all fields must be escaped properly
  const contentRow = [
    escapeCSVField(project.name),
    escapeCSVField(project.base_prompt),
    escapeCSVField(project.model_name),
    escapeCSVField(project.chat_type),
    escapeCSVField(project.rag_api_url || ''),
    escapeCSVField(project.rag_confidence_threshold || 0),
    escapeCSVField(project.rag_token_limit || 0),
    escapeCSVField(project.rag_top_k || 0),
    escapeCSVField(project.rag_vector_db || ''),
    escapeCSVField(project.intro_message || '')
  ].join(',');
  
  // Combine header and data rows
  return [headerRow, contentRow].join('\n');
};

/**
 * Parse CSV content into rows and headers
 */
export const parseCSV = (content: string): { headers: string[], rows: string[][] } => {
  const lines = content.split('\n');
  
  if (lines.length === 0) {
    return { headers: [], rows: [] };
  }
  
  // Extract and process headers from the first line
  const headers = lines[0].split(',');
  
  // Process data rows (everything except the header row)
  const result: string[][] = [];
  let currentRow: string[] = [];
  let insideQuote = false;
  let currentField = '';
  
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    
    for (let j = 0; j < line.length; j++) {
      const char = line[j];
      
      if (char === '"') {
        // If we're already inside a quote and this is a double quote, it's an escaped quote
        if (insideQuote && j < line.length - 1 && line[j + 1] === '"') {
          currentField += '"';
          j++; // Skip the next quote
        } else {
          // Otherwise toggle the quote state
          insideQuote = !insideQuote;
        }
      } else if (char === ',' && !insideQuote) {
        // If we encounter a comma outside quotes, it's a field separator
        currentRow.push(currentField);
        currentField = '';
      } else {
        // Otherwise add the character to the current field
        currentField += char;
      }
    }
    
    // If we're not inside a quote at the end of the line, this is the end of a row
    if (!insideQuote) {
      currentRow.push(currentField);
      result.push(currentRow);
      currentRow = [];
      currentField = '';
    } else {
      // Otherwise, we're in a multi-line field, add a newline
      currentField += '\n';
    }
  }
  
  return {
    headers,
    rows: result
  };
};

/**
 * Create a download for CSV content
 */
export const downloadCSV = (csvContent: string, filename: string): void => {
  // Create a Blob containing the CSV data
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  
  // Create a download link
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  
  // Set link attributes
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  
  // Trigger download
  link.click();
  
  // Clean up
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
